<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { toast } from 'vue-sonner';
import {
  useAsyncOperationSubscription,
  type AsyncOperationFinishedPayload,
} from '@/composables/useAsyncOperationSubscription';
import { useScopedPendingAsyncOperation } from '@/composables/api/useAsyncOperationsApi';
import { useTrackListExportStore } from '@/stores/trackListExportStore';
import type { TrackListExportMeta } from '@/types/trackListExport';

/**
 * Headless watcher for ONE in-flight export. Rendered per operation by
 * {@link TrackListExportHost}, which lives in `App.vue` outside `<RouterView>`.
 *
 * One component per operation on purpose: `useAsyncOperationSubscription`
 * already cleans up in `onUnmounted`, so tying a component's lifetime to a store
 * entry's lifetime makes that existing teardown the leak PREVENTION rather than
 * the leak. No change to the shared composable was needed.
 *
 * Two delivery paths, either of which may win, so both funnel through
 * `finalize()` and an already-handled guard — the same shape
 * `AudioFileIssueInspector` uses for its reruns.
 */
const props = defineProps<{
  operationId: string;
  scope: string;
  label: string;
}>();

const trackListExportStore = useTrackListExportStore();

const hasFinalized = ref(false);

const OPERATION_TYPE = 'track_list_export';

function finalize(status: string | undefined, meta: TrackListExportMeta | undefined): void {
  if (hasFinalized.value) {
    return;
  }
  hasFinalized.value = true;

  if (status === 'succeeded') {
    const downloadUrl = meta?.download_url;
    const rowCount = meta?.row_count ?? 0;

    // "records", not "tracks": this fires for an Account search too, and the
    // toast that STARTED the export already says records — so an Account
    // export announced "3164 records" and then, thirty seconds later,
    // "3164 tracks".
    const description = meta?.email_sent === false
      ? `${rowCount} records. We couldn't email the link, so download it here.`
      : `${rowCount} records. We've emailed you the link too.`;

    toast.success(`${props.label} export is ready`, {
      description,
      duration: 30_000,
      action: downloadUrl
        ? { label: 'Download', onClick: () => window.open(downloadUrl, '_blank', 'noopener') }
        : undefined,
    });
  } else if (status === 'failed') {
    toast.error(`${props.label} export failed`, {
      description: 'Nothing was saved. Try again, or narrow the list if it is very large.',
    });
  } else {
    // Neither Echo nor the poll could tell us the outcome: the operation row's
    // terminal TTL is 30s and browsers throttle timers in background tabs, so
    // this is a routine state, not an error. We genuinely do not know whether it
    // worked — so say the true thing and point at the email, which is sent
    // regardless.
    toast.info(`${props.label} export finished`, {
      description: 'Check your email for the download link.',
    });
  }

  trackListExportStore.forget(props.operationId);
}

// Fallback for a missed broadcast. Stops polling on its own once the payload
// reports not-pending.
const pendingQuery = useScopedPendingAsyncOperation(
  OPERATION_TYPE,
  computed(() => props.scope),
  computed(() => !hasFinalized.value),
);

// The channel is joined only once the poll says the export is still running,
// the rule TrackMatchingPusherMonitor follows. An export restored from
// localStorage after a reload may have finished long ago, and the server
// deletes a finished operation after 30 seconds, so joining it straight away
// would be refused on every reload.
useAsyncOperationSubscription(
  computed(() => (pendingQuery.data.value?.pending === true ? props.operationId : null)),
  {
    onFinished: (payload: AsyncOperationFinishedPayload) => {
      finalize(payload.status, payload.meta as TrackListExportMeta | undefined);
    },
  },
);

watch(
  () => pendingQuery.data.value,
  (pending) => {
    if (!pending || pending.pending) {
      return;
    }

    // `pending: false` with no status means the row has aged out entirely.
    // Treating that as "keep polling" is how a reopened tab ends up polling
    // forever, so it must resolve the entry either way.
    finalize(pending.status, pending.meta as TrackListExportMeta | undefined);
  },
  { immediate: true },
);
</script>

<template>
  <!--
    Headless: this component only subscribes and toasts. A template holding
    nothing but a comment fails eslint's vue/valid-template-root, so it renders
    the same hidden span TrackMatchingPusherMonitor uses for the same reason.
  -->
  <span hidden aria-hidden="true" />
</template>
